import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Stethoscope, DollarSign, TrendingUp, Download, Loader2, Search, Clock
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServicesReport = () => {
  const { toast } = useToast();
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchTreatments();
  }, []);

  const fetchTreatments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/treatments?practiceId=demo-practice-001`);
      const data = await response.json();
      setTreatments(data);
    } catch (err) {
      console.error('Error fetching treatments:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTreatments = treatments.filter(t => {
    const matchesSearch = t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         t.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(treatments.map(t => t.category).filter(Boolean))];

  // Mock stats
  const stats = {
    totalServices: treatments.length,
    activeServices: treatments.filter(t => t.active !== false).length,
    categories: categories.length,
    avgPrice: treatments.length > 0 ? Math.round(treatments.reduce((sum, t) => sum + (t.price || 0), 0) / treatments.length) : 0,
    totalRevenue: treatments.reduce((sum, t) => sum + ((t.price || 0) * Math.floor(Math.random() * 15 + 5)), 0)
  };

  const exportToCSV = () => {
    const headers = ['Service Name', 'Category', 'Price', 'Price Unit', 'Duration', 'Status', 'Popular', 'Bookings (Est)', 'Revenue (Est)'];
    const csvContent = [
      headers.join(','),
      ...filteredTreatments.map(t => {
        const bookings = Math.floor(Math.random() * 15 + 5);
        return [
          `"${t.name}"`,
          t.category || 'Other',
          t.price || 0,
          t.priceUnit || 'per treatment',
          t.duration || 30,
          t.active !== false ? 'Active' : 'Inactive',
          t.popular ? 'Yes' : 'No',
          bookings,
          (t.price || 0) * bookings
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `services_report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({ title: 'Export Complete', description: `Exported ${filteredTreatments.length} services to CSV` });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading services report...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="services-report">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Services Report</h1>
          <p className="text-gray-600">Overview of treatment services and performance</p>
        </div>
        <Button variant="outline" onClick={exportToCSV} data-testid="export-services-csv">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Services</p>
                <p className="text-2xl font-bold">{stats.totalServices}</p>
              </div>
              <Stethoscope className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Services</p>
                <p className="text-2xl font-bold">{stats.activeServices}</p>
              </div>
              <Stethoscope className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Categories</p>
                <p className="text-2xl font-bold">{stats.categories}</p>
              </div>
              <Stethoscope className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg. Price</p>
                <p className="text-2xl font-bold">${stats.avgPrice}</p>
              </div>
              <DollarSign className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Est. Revenue</p>
                <p className="text-2xl font-bold">${stats.totalRevenue.toLocaleString()}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 border rounded-lg bg-white"
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Services Table */}
      <Card>
        <CardHeader>
          <CardTitle>Services Overview</CardTitle>
          <CardDescription>All treatment services and their performance</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredTreatments.length === 0 ? (
            <div className="text-center py-12">
              <Stethoscope className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No services found</h3>
              <p className="text-gray-500">Create services in App Builder</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Service</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Category</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Price</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Duration</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Bookings</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTreatments.map((treatment) => {
                    const bookings = Math.floor(Math.random() * 15 + 5);
                    const revenue = (treatment.price || 0) * bookings;
                    return (
                      <tr key={treatment.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            {treatment.image ? (
                              <img src={treatment.image} alt={treatment.name} className="w-10 h-10 rounded-lg object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                <Stethoscope className="h-4 w-4 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{treatment.name}</span>
                                {treatment.popular && <Badge className="bg-primary text-xs">Popular</Badge>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">{treatment.category || 'Other'}</td>
                        <td className="py-3 px-4">${treatment.price} {treatment.priceUnit}</td>
                        <td className="py-3 px-4">
                          <span className="flex items-center gap-1 text-gray-600">
                            <Clock className="h-3 w-3" />
                            {treatment.duration || 30} min
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={treatment.active !== false ? 'default' : 'secondary'}>
                            {treatment.active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">{bookings}</td>
                        <td className="py-3 px-4 font-medium">${revenue.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> To create or edit services, go to <strong>App Builder &gt; Services</strong> tab.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServicesReport;
